import {
    autoUpdate,
    FloatingPortal,
    flip,
    offset as offsetMiddleware,
    type Placement,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole,
    useTransitionStyles,
} from "@floating-ui/react";
import { cn } from "@heroui/react";
import {
    cloneElement,
    type FC,
    isValidElement,
    type ReactElement,
    type ReactNode,
    type Ref,
    useCallback,
    useState,
} from "react";

// Our own tooltip on Floating UI; HeroUI's is not to be used (see the lint
// rule in biome.jsonc). HeroUI's runs on React Aria, which opens every further
// tooltip without any delay once one was shown: moving the pointer over a
// table or a row of icons flashed each tooltip on the way for a few
// milliseconds. Here `delay` is how long the pointer has to rest on the trigger
// (Floating UI's `restMs`), so a pointer passing over shows nothing.

const colorClassNames = {
    primary: "border-l-4 border-primary",
    secondary: "border-l-4 border-secondary",
    warning: "border-l-4 border-warning",
    danger: "border-l-4 border-danger",
};

interface TooltipProps {
    content: ReactNode;
    children: ReactNode;
    color?: keyof typeof colorClassNames;
    placement?: Placement;
    // Rest time of the pointer on the trigger before the tooltip opens.
    delay?: number;
    closeDelay?: number;
    offset?: number;
    crossOffset?: number;
    isDisabled?: boolean;
    // Set to control the tooltip from outside (a "Kopiert" after a click).
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    // Content the pointer can move into and click (a list of links).
    isInteractive?: boolean;
    className?: string;
    classNames?: { base?: string; content?: string };
}

// Only one tooltip is open at a time. A tooltip whose trigger holds the open
// one (a name cell around its duplicates icon) leaves it be.
let openTooltip: { reference: Element | null; close: () => void } | null = null;

const Tooltip: FC<TooltipProps> = ({
    content,
    children,
    color,
    placement = "top",
    delay = 300,
    closeDelay = 100,
    offset = 7,
    crossOffset = 0,
    isDisabled = false,
    isOpen: isOpenProp,
    onOpenChange,
    isInteractive = false,
    className,
    classNames = {},
}) => {
    const [isOpenState, setIsOpenState] = useState(false);
    const isControlled = isOpenProp !== undefined;
    const isOpen = (isControlled ? isOpenProp : isOpenState) && !isDisabled;

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => setOpen(open),
        placement,
        middleware: [
            offsetMiddleware({ mainAxis: offset, crossAxis: crossOffset }),
            flip({ padding: 12 }),
            shift({ padding: 12 }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const setOpen = useCallback(
        (open: boolean) => {
            const reference = refs.domReference.current;

            if (open) {
                if (
                    openTooltip &&
                    openTooltip.reference !== reference &&
                    reference?.contains(openTooltip.reference)
                ) {
                    return;
                }

                if (openTooltip?.reference !== reference) {
                    openTooltip?.close();
                }
                openTooltip = { reference, close: () => setOpen(false) };
            } else if (openTooltip?.reference === reference) {
                openTooltip = null;
            }

            setIsOpenState(open);
            onOpenChange?.(open);
        },
        [refs, onOpenChange],
    );

    const isEnabled = !isDisabled && !isControlled;
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, {
            enabled: isEnabled,
            restMs: delay,
            delay: { close: closeDelay },
            mouseOnly: true,
            handleClose: isInteractive ? safePolygon() : null,
        }),
        useFocus(context, { enabled: isEnabled, visibleOnly: true }),
        // A click on the trigger hides the tooltip.
        useDismiss(context, { referencePress: true }),
        useRole(context, { role: "tooltip" }),
    ]);

    const { isMounted, styles } = useTransitionStyles(context, {
        duration: { open: 150, close: 100 },
        initial: { opacity: 0, transform: "scale(0.97)" },
    });

    const child = isValidElement(children)
        ? (children as ReactElement<{ ref?: Ref<Element> }>)
        : null;
    const ref = useMergeRefs([refs.setReference, child?.props.ref]);

    const trigger = child ? (
        cloneElement(child, getReferenceProps({ ...child.props, ref }))
    ) : (
        <p ref={ref} {...getReferenceProps()}>
            {children}
        </p>
    );

    return (
        <>
            {trigger}
            {isMounted && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        // Decoration must not catch the pointer: over a table
                        // it floats above the neighbouring row and would
                        // swallow the click meant for it.
                        className={cn(
                            "z-50 max-w-lg",
                            !isInteractive && "pointer-events-none",
                            classNames.base,
                        )}
                    >
                        <div
                            style={styles}
                            className={cn(
                                "box-border inline-flex flex-col items-center justify-center rounded-medium bg-content1 p-3 text-small subpixel-antialiased shadow-small",
                                color && colorClassNames[color],
                                classNames.content,
                                className,
                            )}
                        >
                            {content}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

export { Tooltip, type TooltipProps };
